// Approved answers only — every answer mirrors wording already published on the site.
// Nothing here collects, transmits, or stores personal information.

export const GREETING =
  "Welcome aboard! I'm a guided helper that answers from approved information on the Chaplain at Sea site. Ask about who the retreat is for, the themed sailings, life aboard, or how to express interest.";

export const DEFAULT_CHIPS = [
  "Who is the retreat for?",
  "What are the different sailings?",
  "What can I do aboard?",
  "How do I express interest?",
  "Are dates and prices confirmed?",
];

export const INTEREST_URL = "https://chaplainatsea.com/interest-form";

export const FALLBACK =
  "I'm not sure about that one — I can only answer from approved information about Chaplain at Sea. Try one of the questions below, or share your interest on the form and we'll keep you updated as plans are confirmed.";

export const TOPICS = [
  {
    id: "privacy",
    keywords: ["chatbot", "bot", "chat", "conversation", "stored", "saved", "privacy", "private", "delete", "gdpr", "name", "email", "phone", "contact details"],
    answer:
      "This chat is just a guided helper: nothing you type is sent anywhere or saved — the conversation disappears when you close it. We never ask for names or contact details here. To receive updates, use the interest form linked below.",
  },
  {
    id: "concert",
    keywords: ["concert", "music", "singer", "sing", "performer", "worship event", "evening activity", "entertainment"],
    answer:
      "A live concert or inspirational music event is a possibility for a special evening during the retreat — kept separate from the daytime retreat sessions. It is not confirmed: the performer and details are to be announced, and nothing is promised or included in any price at this stage. Either way, guests will keep free time to enjoy the ship and ports.",
  },
  {
    id: "prices",
    keywords: ["price", "prices", "pricing", "cost", "how much", "fare", "cabin", "cabins", "room options", "payment", "pay", "deposit", "book", "booking", "reserve", "reservation", "ticket", "buy"],
    answer:
      "Cabin options, rates, and payment details are to be announced. No deposits or reservations are being taken, and nothing can be booked or paid through this chat. Sharing your interest on the form is free and carries no obligation — it is an interest form, not a booking.",
  },
  {
    id: "dates",
    keywords: ["date", "dates", "when", "ship", "length", "how long", "days", "departure", "depart", "leave from", "itinerary", "route", "ports", "destination", "destinations", "catalina", "ensenada", "los angeles"],
    answer:
      "Those details are to be announced. The ship, exact dates, length, departure port, route, and ports of call have not been confirmed yet — final plans will be shared when confirmed. The best way to hear updates is the interest form (linked below), which carries no booking or payment.",
  },
  {
    id: "sailings",
    keywords: ["sailing", "sailings", "theme", "themes", "different", "remembrance", "grief", "exploring chaplaincy", "renewal at sea", "diagnosis"],
    answer:
      "Three separate themed sailings are in development, each its own dedicated cruise with a light, theme-specific program across 3-, 4-, or 5-day itineraries: Chaplain Renewal at Sea (rest and renewal for chaplains), Exploring Chaplaincy at Sea (for pastors and church elders exploring chaplaincy), and From Diagnosis to Goodbye: Grief and Remembrance at Sea (reflection and remembrance, with a possible optional celebration-of-life gathering). Ships, dates, and itineraries are still to be announced.",
  },
  {
    id: "about",
    keywords: ["host", "derrick", "hall", "about", "chaplain at sea", "what is", "who are you", "ccchaps", "fear", "afraid", "scared", "ovation", "icon of the seas", "first-time", "first time", "cruise experience"],
    answer:
      "Chaplain Derrick Hall has served in ministry for more than 25 years, encouraging chaplains and caregivers across healthcare, law enforcement, the military, ministry, and other fields. The idea for Chaplain at Sea grew from his own experience: long afraid of the open sea, he eventually sailed on Royal Caribbean's Ovation of the Seas and later Icon of the Seas — finding quiet balcony moments with tea, Scripture, prayer, and reflection. Now he hopes to offer chaplains a similar chance at rest, fellowship, and renewal, with practical guidance for first-time cruisers. His message: caring for ourselves isn't selfish.",
  },
  {
    id: "who_for",
    keywords: ["who", "for", "welcome", "exploring", "pastor", "pastors", "elder", "elders", "qualify", "attend", "eligib", "not a chaplain"],
    answer:
      "Chaplain at Sea retreats are being planned for practicing chaplains — and for pastors, church elders, and anyone exploring a calling to chaplaincy. You don't have to be a currently serving chaplain to join the interest list, and attendance doesn't imply certification, endorsement, or employment of any kind.",
  },
  {
    id: "aboard",
    keywords: ["aboard", "activities", "activity", "daily", "schedule", "program", "sessions", "class", "classes", "workshop", "rest", "journal", "speaker", "worship", "shore"],
    answer:
      "Days stay light and optional: quiet time in your room or balcony, encouragement from a guest speaker, worship and reflection, community and connection, and time to enjoy the ship and shore excursions. Optional planned classes may include Caring for the Caregiver, Finding Stillness at Sea, Your Story Matters, Legacy and Spiritual Journaling, Exploring Chaplaincy, and Sunset Gratitude Circle. A live concert or inspirational music event is a possibility for a special evening — not confirmed, with details to be announced.",
  },
  {
    id: "interest",
    keywords: ["interest", "sign up", "signup", "join", "express", "update", "updates", "list", "form", "newsletter", "hear more", "stay informed"],
    answer:
      "You can share your interest any time on our interest form — it is an interest form, not a booking, and joining carries no payment or reservation. Tap \"Open the interest form\" below, or use the \"Share your interest\" / \"Join the Interest List\" buttons on the homepage.",
  },
  {
    id: "passport",
    keywords: ["passport", "travel document", "documents", "visa", "customs"],
    answer:
      "A valid passport book will be required for participants. Final travel-document instructions will be provided once the cruise and itinerary are confirmed. The State Department notes that cruise lines may require a passport book and recommends one for cruise passengers.",
  },
];

export function matchTopic(raw) {
  const hay =
    " " +
    String(raw)
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, " ")
      .replace(/\s+/g, " ")
      .trim() +
    " ";
  if (hay.trim().length < 2) return { answer: FALLBACK, chips: DEFAULT_CHIPS };

  let best = null;
  let bestScore = 0;
  for (const topic of TOPICS) {
    let score = 0;
    for (const k of topic.keywords) {
      if (hay.includes(" " + k + " ") || hay.includes(" " + k)) score += 1;
    }
    if (score > bestScore) {
      best = topic;
      bestScore = score;
    }
  }

  if (!best || bestScore < 1) return { answer: FALLBACK, chips: DEFAULT_CHIPS };
  const followUps = DEFAULT_CHIPS.filter((c) => c.toLowerCase() !== raw.toLowerCase()).slice(0, 3);
  return { answer: best.answer, chips: followUps };
}
